package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import com.fantasytenis.shared.data.bracket.repository.PickInput
import com.fantasytenis.web.di.DependencyContainer
import kotlinx.coroutines.*
import kotlin.math.pow

/**
 * Save status for the auto-save indicator.
 */
sealed class SaveStatus {
    object Idle : SaveStatus()
    object Saving : SaveStatus()
    data class Saved(val timestamp: Long = currentTimeMillis()) : SaveStatus()
    data class Error(val message: String, val retryCount: Int = 0) : SaveStatus()
}

/**
 * Result of the auto-save hook.
 */
data class AutoSaveState(
    val status: SaveStatus,
    val lastSaveTime: Long?,
    val pendingChanges: Boolean
)

/**
 * Configuration for auto-save behavior.
 */
data class AutoSaveConfig(
    val debounceMs: Long = 300L,
    val maxRetries: Int = 3,
    val retryDelayMs: Long = 1000L,
    val retryBackoffMultiplier: Double = 2.0
)

/**
 * Hook for auto-saving bracket picks with debouncing and retry logic.
 *
 * Features:
 * - Debounced saves (300ms by default)
 * - Optimistic UI updates
 * - Retry with exponential backoff on failure
 * - Status tracking for UI feedback
 *
 * @param entryId The bracket entry ID
 * @param config Auto-save configuration
 * @return AutoSaveState and a save function
 */
@Composable
fun useAutoSavePicks(
    entryId: String,
    config: AutoSaveConfig = AutoSaveConfig()
): Pair<AutoSaveState, (Map<String, String>) -> Unit> {
    val scope = rememberCoroutineScope()

    var status by remember { mutableStateOf<SaveStatus>(SaveStatus.Idle) }
    var lastSaveTime by remember { mutableStateOf<Long?>(null) }
    var pendingChanges by remember { mutableStateOf(false) }
    var debounceJob by remember { mutableStateOf<Job?>(null) }

    // Track the latest picks to save
    var latestPicks by remember { mutableStateOf<Map<String, String>?>(null) }

    // The actual save function - use suspend function that handles retries
    suspend fun performSave(picks: Map<String, String>, retryCount: Int) {
        status = SaveStatus.Saving

        val pickInputs = picks.map { (posId, winnerId) ->
            PickInput(positionId = posId, predictedWinnerId = winnerId)
        }

        DependencyContainer.submitBracketPicksUseCase(entryId, pickInputs).fold(
            onSuccess = {
                val now = currentTimeMillis()
                status = SaveStatus.Saved(now)
                lastSaveTime = now
                pendingChanges = false
            },
            onFailure = { error ->
                if (retryCount < config.maxRetries) {
                    // Schedule retry with backoff
                    val delayMs = (config.retryDelayMs * config.retryBackoffMultiplier.pow(retryCount.toDouble())).toLong()
                    status = SaveStatus.Error(
                        message = "Save failed, retrying...",
                        retryCount = retryCount + 1
                    )
                    delay(delayMs)
                    // Retry with latest picks (may have changed during delay)
                    latestPicks?.let { performSave(it, retryCount + 1) }
                } else {
                    status = SaveStatus.Error(
                        message = error.message ?: "Failed to save picks",
                        retryCount = retryCount
                    )
                }
            }
        )
    }

    // Debounced save trigger
    val triggerSave: (Map<String, String>) -> Unit = { picks ->
        latestPicks = picks
        pendingChanges = true

        // Cancel any pending debounce
        debounceJob?.cancel()

        // Start new debounce
        debounceJob = scope.launch {
            delay(config.debounceMs)
            latestPicks?.let { performSave(it, 0) }
        }
    }

    // Cleanup on unmount
    DisposableEffect(Unit) {
        onDispose {
            debounceJob?.cancel()
        }
    }

    val state = AutoSaveState(
        status = status,
        lastSaveTime = lastSaveTime,
        pendingChanges = pendingChanges
    )

    return Pair(state, triggerSave)
}

/**
 * Force an immediate save without debouncing.
 * Useful for the "Submit Bracket" action.
 */
suspend fun forceImmediateSave(
    entryId: String,
    picks: Map<String, String>
): Result<Unit> {
    val pickInputs = picks.map { (posId, winnerId) ->
        PickInput(positionId = posId, predictedWinnerId = winnerId)
    }

    return DependencyContainer.submitBracketPicksUseCase(entryId, pickInputs).map { }
}

// Platform-specific time function
private fun currentTimeMillis(): Long = js("Date.now()").unsafeCast<Long>()

/**
 * Format a timestamp as a relative time string.
 */
fun formatRelativeTime(timestamp: Long): String {
    val now = currentTimeMillis()
    val diffMs = now - timestamp
    val diffSeconds = diffMs / 1000
    val diffMinutes = diffSeconds / 60

    return when {
        diffSeconds < 5 -> "just now"
        diffSeconds < 60 -> "${diffSeconds}s ago"
        diffMinutes < 60 -> "${diffMinutes}m ago"
        else -> "${diffMinutes / 60}h ago"
    }
}
